import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { useEffectiveRoles } from '../hooks/useEffectiveRoles';
import { useTenantPath } from '../hooks/useTenant';
import { logWarn } from '../utils/logger';

/**
 * RoleGuard – schützt Routen anhand von effectiveRoles.
 *
 * Rendert children nur, wenn mind. eine der effectiveRoles des Benutzers
 * in der allow-Liste enthalten ist.
 *
 * Sonderfall Backwards-Compatibility: Wenn effectiveRoles leer ist (Legacy-Token
 * ohne das Feld), wird der Zugriff erlaubt (alte User sind keine RENTER).
 * In diesem Fall wird eine Console-Warnung ausgegeben (einmalig pro Mount).
 *
 * Für RENTER-User die nicht erlaubte Routen aufrufen: Weiterleitung nach /contracts.
 *
 * @param {object} props
 * @param {string[]} props.allow  – Rollen, die Zugriff erhalten (z.B. ['OWNER','ADMIN','MEMBER'])
 * @param {React.ReactNode} props.children
 */
const RoleGuard = ({ allow, children }) => {
  const effectiveRoles = useEffectiveRoles();
  const { buildPath } = useTenantPath();
  // One-shot-Guard: verhindert mehrfaches logWarn bei Re-Renders
  const warnedRef = useRef(false);

  // Backwards-Compatibility: leeres effectiveRoles-Array bedeutet Legacy-Token
  if (effectiveRoles.length === 0) {
    if (!warnedRef.current) {
      warnedRef.current = true;
      logWarn(
        '[RoleGuard] effectiveRoles ist leer – Token enthält kein Rollen-Feld. ' +
        'Zugriff wird für Backwards-Compatibility gewährt. ' +
        'Bitte nach erneutem Login prüfen.'
      );
    }
    return children;
  }

  const hasPermission = effectiveRoles.some(role => allow.includes(role));
  if (!hasPermission) {
    return <Navigate to={buildPath('/contracts')} replace />;
  }

  return children;
};

RoleGuard.propTypes = {
  allow: PropTypes.arrayOf(PropTypes.string).isRequired,
  children: PropTypes.node.isRequired
};

export default RoleGuard;
